/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines */
import React from 'react';
import {
  FaPlug,
  FaFilePowerpoint,
  FaDesktop,
  FaDownload,
  FaUpload,
  FaVideo,
  FaVolumeUp,
} from 'react-icons/fa';
import CircleIcon from '@mui/icons-material/Circle';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { StyledBox } from '../../styled';
import { IConfigOption } from '../../types';
import { BrowserIcon } from '../BrowserIcon';
import {
  audioBitrateClass,
  downloadSpeedClass,
  uploadSpeedClass,
  videoBitrateClass,
} from '../helpers';
import { SummaryListItem } from './SummaryListItem';
import { SummaryTile } from './SummaryTile';

interface ISummaryTilesProps {
  audioAverageKbps: number;
  audioOnly: boolean;
  browserInfo: {
    name: string;
    version: string;
  } | null;
  configurationOptions?: IConfigOption;
  connectivitySuccessfull: boolean;
  downloadSpeed: number;
  hideKey: boolean;
  isValidBrowser: boolean;
  liveSlidesValid?: boolean;
  screenshareSupported: boolean;
  uploadSpeed: number;
  videoAverageKbps: number;
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
}));

const StyledList = styled(List)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(1)} 0`,
}));

export const SummaryTiles = ({
  audioAverageKbps,
  audioOnly,
  browserInfo,
  configurationOptions,
  connectivitySuccessfull,
  downloadSpeed,
  hideKey,
  isValidBrowser,
  liveSlidesValid,
  screenshareSupported,
  uploadSpeed,
  videoAverageKbps,
}: ISummaryTilesProps) => {
  const tileCount =
    3 +
    (configurationOptions?.liveSlides ? 1 : 0) +
    (!audioOnly ? 3 : 2) +
    (configurationOptions?.screenshare ? 1 : 0);

  return (
    <>
      <StyledGrid container={true} spacing={2}>
        <SummaryTile
          subTitle={browserInfo?.name}
          tileClass={isValidBrowser ? 'very-good' : 'bad'}
          tileCount={tileCount}
          tileIcon={<BrowserIcon browserName={browserInfo?.name} />}
          title="Browser"
        />

        <SummaryTile
          subTitle={connectivitySuccessfull ? 'Successful' : 'Unsuccessful'}
          tileClass={connectivitySuccessfull ? 'very-good' : 'bad'}
          tileCount={tileCount}
          tileIcon={<FaPlug />}
          title="Connectivity"
        />

        {configurationOptions?.liveSlides ? (
          <SummaryTile
            subTitle={liveSlidesValid ? 'Success' : 'Failed'}
            tileClass={liveSlidesValid ? 'very-good' : 'bad'}
            tileCount={tileCount}
            tileIcon={<FaFilePowerpoint />}
            title="Live Slides"
          />
        ) : null}

        {configurationOptions?.screenshare ? (
          <SummaryTile
            subTitle={screenshareSupported ? 'Successful' : 'Unsuccessful'}
            tileClass={screenshareSupported ? 'very-good' : 'bad'}
            tileCount={tileCount}
            tileIcon={<FaDesktop />}
            title="Screenshare"
          />
        ) : null}

        {downloadSpeed ? (
          <SummaryTile
            subTitle={`${downloadSpeed.toFixed(1)}Mbps`}
            tileClass={downloadSpeedClass(downloadSpeed)}
            tileCount={tileCount}
            tileIcon={<FaDownload />}
            title="Download"
          />
        ) : null}

        {uploadSpeed ? (
          <SummaryTile
            subTitle={`${uploadSpeed.toFixed(1)}Mbps`}
            tileClass={uploadSpeedClass(uploadSpeed)}
            tileCount={tileCount}
            tileIcon={<FaUpload />}
            title="Upload"
          />
        ) : null}

        {!audioOnly && videoAverageKbps !== undefined ? (
          <SummaryTile
            subTitle={`${videoAverageKbps.toFixed(1)}Kbps`}
            tileClass={videoBitrateClass(videoAverageKbps)}
            tileCount={tileCount}
            tileIcon={<FaVideo />}
            title="Video Bitrate"
          />
        ) : null}

        {audioAverageKbps !== undefined ? (
          <SummaryTile
            subTitle={`${audioAverageKbps.toFixed(1)}Kbps`}
            tileClass={audioBitrateClass(audioAverageKbps)}
            tileCount={tileCount}
            tileIcon={<FaVolumeUp />}
            title="Audio Bitrate"
          />
        ) : null}
      </StyledGrid>

      <StyledBox>
        <Box sx={{ padding: 2 }}>
          <Grid container={true}>
            <Grid item={true} md={6} xs={12}>
              <Typography variant="h6">Testing Summary</Typography>
            </Grid>
            <Grid item={true} md={6} xs={12}>
              {!hideKey ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {['very-good', 'good', 'average', 'bad'].map(key => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 1,
                      }}
                    >
                      <CircleIcon className={key} fontSize="small" />
                      <Typography sx={{ marginLeft: 0.5 }} variant="body2">
                        {key.replace('-', ' ').charAt(0).toUpperCase() +
                          key.slice(1)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Grid>
          </Grid>

          <StyledList>
            <SummaryListItem
              isSuccess={isValidBrowser}
              primaryText={
                <>
                  <b>Browser compatibility - </b>Your browser is{' '}
                  <b className={isValidBrowser ? 'very-good' : 'bad'}>
                    {isValidBrowser ? 'compatible' : 'incompatible'}
                  </b>{' '}
                  <BrowserIcon browserName={browserInfo?.name} />{' '}
                  {browserInfo?.name}
                  {browserInfo && (
                    <span> (Version: {browserInfo.version})</span>
                  )}
                </>
              }
              secondaryText={
                <>
                  For best performance we recommend the <b>latest version</b> of{' '}
                  <b>Chrome on PC/Mac/Android</b> and <b>Safari on iOS</b>
                </>
              }
            />

            <SummaryListItem
              isSuccess={connectivitySuccessfull}
              primaryText={
                <>
                  <b>Connectivity - </b>
                  {connectivitySuccessfull ? (
                    <span>
                      You have <b className="very-good">successfully</b> been
                      able to join a conference
                    </span>
                  ) : (
                    <span>
                      You have been <b className="bad">unsuccessful</b> in
                      joining a conference
                    </span>
                  )}
                </>
              }
              secondaryText={
                <>
                  {connectivitySuccessfull
                    ? 'You should have no issues when joining the webcam conference'
                    : 'There has been an issue when joining the conference'}
                </>
              }
            />

            {configurationOptions?.liveSlides ? (
              <SummaryListItem
                isSuccess={Boolean(liveSlidesValid)}
                primaryText={
                  <>
                    <b>Live Slides - </b>
                    <span>
                      You have{' '}
                      <b className={liveSlidesValid ? 'very-good' : 'bad'}>
                        {liveSlidesValid ? 'successfully' : 'unsuccessfully'}
                      </b>{' '}
                      connected to our live slides server
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    Please ensure you use a modern browser and that websockets
                    are turned on
                  </>
                }
              />
            ) : null}

            {configurationOptions?.screenshare ? (
              <SummaryListItem
                isSuccess={screenshareSupported}
                primaryText={
                  <>
                    <b>Screenshare - </b>
                    <span>
                      Your device is{' '}
                      <b className={screenshareSupported ? 'very-good' : 'bad'}>
                        {screenshareSupported ? 'compatible' : 'incompatible'}
                      </b>{' '}
                      with screenshare
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    Please ensure you use a modern device on a desktop computer
                    for screenshare
                  </>
                }
              />
            ) : null}

            {downloadSpeed ? (
              <SummaryListItem
                isSuccess={downloadSpeed > 5}
                primaryText={
                  <>
                    <b>Download Speed - </b>
                    <span>
                      You have a{' '}
                      <b className={downloadSpeedClass(downloadSpeed)}>
                        {downloadSpeed > 20
                          ? 'very good'
                          : downloadSpeed > 10
                          ? 'good'
                          : downloadSpeed > 5
                          ? 'average'
                          : 'poor'}
                      </b>{' '}
                      download speed of{' '}
                      <b className={downloadSpeedClass(downloadSpeed)}>
                        {downloadSpeed.toFixed(2)}Mbps
                      </b>
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    For best performance we recommend at least <b>20Mbps</b>{' '}
                    download speed
                  </>
                }
              />
            ) : null}

            {uploadSpeed ? (
              <SummaryListItem
                isSuccess={uploadSpeed > 4}
                primaryText={
                  <>
                    <b>Upload Speed - </b>
                    <span>
                      You have a{' '}
                      <b className={uploadSpeedClass(uploadSpeed)}>
                        {uploadSpeed > 10
                          ? 'very good'
                          : uploadSpeed > 8
                          ? 'good'
                          : uploadSpeed > 4
                          ? 'average'
                          : 'poor'}
                      </b>{' '}
                      upload speed of{' '}
                      <b className={uploadSpeedClass(uploadSpeed)}>
                        {uploadSpeed.toFixed(2)}Mbps
                      </b>
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    For best performance we recommend at least <b>10Mbps</b>{' '}
                    upload speed
                  </>
                }
              />
            ) : null}

            {!audioOnly && videoAverageKbps ? (
              <SummaryListItem
                isSuccess={videoAverageKbps > 200}
                primaryText={
                  <>
                    <b>Video Bitrate - </b>
                    <span>
                      You have a{' '}
                      <b className={videoBitrateClass(videoAverageKbps)}>
                        {videoAverageKbps > 600
                          ? 'very good'
                          : videoAverageKbps > 400
                          ? 'good'
                          : videoAverageKbps > 200
                          ? 'average'
                          : 'poor'}
                      </b>{' '}
                      video bitrate of{' '}
                      <b className={videoBitrateClass(videoAverageKbps)}>
                        {videoAverageKbps.toFixed(0)}Kbps
                      </b>
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    For best performance we recommend <b>600Kbps</b> video
                  </>
                }
              />
            ) : null}

            {audioAverageKbps ? (
              <SummaryListItem
                isSuccess={audioAverageKbps > 10}
                primaryText={
                  <>
                    <b>Audio Bitrate - </b>
                    <span>
                      You have a{' '}
                      <b className={audioBitrateClass(audioAverageKbps)}>
                        {audioAverageKbps > 25
                          ? 'very good'
                          : audioAverageKbps > 15
                          ? 'good'
                          : audioAverageKbps > 10
                          ? 'average'
                          : 'poor'}
                      </b>{' '}
                      audio bitrate of{' '}
                      <b className={audioBitrateClass(audioAverageKbps)}>
                        {audioAverageKbps.toFixed(0)}Kbps
                      </b>
                    </span>
                  </>
                }
                secondaryText={
                  <>
                    For best performance we recommend <b>25Kbps</b> audio
                  </>
                }
              />
            ) : null}
          </StyledList>
        </Box>
      </StyledBox>
    </>
  );
};
